import { AxiosError } from 'axios'

// URL query keys that hold the active barcode search. Kept in the URL (like the
// rest of the list state) so the table auto-refetches on change.
// - BARCODE_QUERY_KEY: typed/pasted barcodes (comma-separated) → exact `$in`.
// - BARCODE_TOKEN_KEY: token from an uploaded file (large lists).
export const BARCODE_QUERY_KEY = 'barcode'
export const BARCODE_TOKEN_KEY = 'barcodeToken'
export const BARCODE_COUNT_KEY = 'barcodeCount'

/**
 * Splits a typed/pasted barcode string into a clean, de-duplicated list.
 * Separators are commas and any whitespace (so pasting a spreadsheet column
 * works too); blank entries are dropped.
 */
export const normalizeTypedBarcodes = (input: string): string[] =>
  Array.from(new Set(input.split(/[\s,]+/).filter(Boolean)))

// Backend code returned once the 60-minute token has expired.
export const BARCODE_SEARCH_EXPIRED = 'BARCODE_SEARCH_EXPIRED'

export const MAX_FILE_SIZE_MB = 6
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

// Backend error codes → friendly copy. Covers both the Catalogue and Inventory
// endpoints (Inventory adds INVALID_FILE_TYPE / LIMIT_CHUNK_SIZE).
export const BARCODE_ERROR_MESSAGES: Record<string, string> = {
  BUFFER_MISSING: 'Please choose a file.',
  NO_BARCODES_IN_FILE: 'The file has no barcodes. Please check the format.',
  BARCODE_LIMIT_EXCEEDED:
    'Max 50,000 barcodes per file. Please split the file.',
  INVALID_FILE_TYPE: 'Only CSV files are allowed.',
  LIMIT_CHUNK_SIZE: `File is too large (max ${MAX_FILE_SIZE_MB} MB).`,
  [BARCODE_SEARCH_EXPIRED]:
    'Barcode search expired — please upload the file again.',
}

/**
 * Reads the backend error code off an axios error, tolerating both envelopes:
 *  - Catalogue: `{ message: 'CODE' }`
 *  - Inventory: `{ success: false, errors: { msg: 'CODE' | { code: 'CODE' } } }`
 *    (the size/type errors arrive as an object with its own `code`).
 */
export const getBarcodeErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof AxiosError)) return undefined

  const data = error.response?.data
  const msg = data?.errors?.msg

  if (typeof msg === 'string') return msg
  if (msg && typeof msg === 'object' && typeof msg.code === 'string') {
    return msg.code
  }
  if (typeof data?.message === 'string') return data.message

  return undefined
}
